import React, { useEffect, useState } from 'react';
import { AlertTriangle, Save, Paperclip, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { ValidatedTextarea } from '../../../validation/ValidatedFields';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import {
  findingService,
  findingEvidenceService,
  type Finding,
  type FindingCreatePayload,
} from '../../../services/auditService';
import { ITEM_TYPE_RISK, ITEM_TYPE_CONTROL } from './RaiseFindingButton';

type BridgeSeverity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational';
const BRIDGE_SEVERITIES: BridgeSeverity[] = ['Critical', 'High', 'Medium', 'Low', 'Informational'];

/**
 * Plan 05b-05 / STAFF-06 — Inline ObservationModal.
 *
 * Opened by RaiseFindingButton from any transactional response row. Captures:
 *   - read-only row context (procedure name + sample label + response value)
 *   - findings_text (required, >=10 chars)
 *   - severity toggle (Material default; Trivial)
 *   - optional single evidence file (via fileService.upload)
 *
 * On submit:
 *   1. If a file is present, fileService.upload runs first.
 *   2. findingService.create posts the finding with polymorphic item linkage.
 *
 * Errors stay in the modal — Submit re-enables so the user can retry.
 *
 * Phase 06 UI polish (2026-05-20): inline-style sprawl replaced with
 * token-driven CSS classes (.observation-*, .severity-toggle-*) per
 * 05b/06-UI-REVIEW. Buttons swapped to shared Button compound. Internal
 * versioning copy ("v1/v2") replaced with user-facing language.
 */

export interface ObservationModalRowContext {
  procedureName: string;
  sampleLabel?: string | null;
  responseValue?: string | null;
}

export interface ObservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  procedureInstanceId: number;
  entityId: number;
  currentUserId: number;
  rowContext: ObservationModalRowContext;
  /** Polymorphic id pair (item_type_id constants live in RaiseFindingButton.tsx). */
  itemTypeId?: number | null;
  /** Points at the response row id (preferred) OR the sample item id. */
  itemRecordId?: number | null;
  onCreated: (finding: Finding) => void;
}

// Findings only require non-empty text (engine code 110 caps at 2000 chars and
// blocks angle brackets). No artificial minimum length — short findings are valid.
const MIN_FINDING_LENGTH = 1;

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const ObservationModal: React.FC<ObservationModalProps> = ({
  isOpen,
  onClose,
  procedureInstanceId,
  entityId,
  currentUserId,
  rowContext,
  itemTypeId,
  itemRecordId,
  onCreated,
}) => {
  const fieldVh = useFieldValidation();
  // A control/risk finding ALSO creates a control deficiency / risk vulnerability,
  // which carry a richer 5-level severity + an optional title — surface those here.
  const isRiskOrControl = itemTypeId === ITEM_TYPE_RISK || itemTypeId === ITEM_TYPE_CONTROL;
  const bridgeNoun = itemTypeId === ITEM_TYPE_CONTROL ? 'Deficiency' : 'Vulnerability';
  const [findingsText, setFindingsText] = useState<string>('');
  const [isTrivial, setIsTrivial] = useState<boolean>(false);
  const [bridgeSeverity, setBridgeSeverity] = useState<BridgeSeverity>('Medium');
  const [bridgeTitle, setBridgeTitle] = useState<string>('');
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state when the modal (re-)opens.
  useEffect(() => {
    if (!isOpen) return;
    setFindingsText('');
    setIsTrivial(false);
    setBridgeSeverity('Medium');
    setBridgeTitle('');
    setEvidenceFile(null);
    setEvidenceOpen(false);
    setSubmitting(false);
    setError(null);
    fieldVh.reset();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = findingsText.trim();
  const canSubmit = !submitting && trimmed.length >= MIN_FINDING_LENGTH;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Validate findings_text (code 110: description, required)
    const engineOk = fieldVh.validateAll([
      { name: 'findings_text', code: 110, value: findingsText, required: true },
    ]);
    if (!engineOk) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: FindingCreatePayload = {
        entity_id: entityId,
        procedure_instance_id: procedureInstanceId,
        item_type_id: itemTypeId ?? null,
        item_record_id: itemRecordId ?? null,
        findings_text: trimmed,
        raised_on_date: todayIso(),
        response_user_id: currentUserId,
        // For control/risk findings the 5-level severity drives the bridged
        // deficiency/vulnerability AND a sensible is_trivial (Critical/High = Material).
        is_trivial: isRiskOrControl ? !(bridgeSeverity === 'Critical' || bridgeSeverity === 'High') : isTrivial,
        is_closed: false,
        is_active: 1,
        created_by: currentUserId,
        ...(isRiskOrControl
          ? { bridge_severity: bridgeSeverity, bridge_title: bridgeTitle.trim() || null }
          : {}),
      };
      const finding = await findingService.create(payload);

      // Attach evidence AFTER the finding exists — needs finding.id. The previous
      // code uploaded the bytes with fileService.upload BEFORE create and discarded
      // the returned id, so the file was never linked into ADT_finding_evidence and
      // memo/finding detail showed no evidence. findingEvidenceService.upload uploads
      // AND links in one call.
      if (evidenceFile) {
        try {
          await findingEvidenceService.upload(evidenceFile, finding.id, entityId, currentUserId);
        } catch (uploadErr: any) {
          // Finding is already saved — register it and surface a non-blocking note
          // so a retry doesn't create a duplicate finding.
          // eslint-disable-next-line no-console
          console.error('Evidence attach failed for finding', finding.id, uploadErr);
          setError('Finding raised, but the evidence failed to attach — add it from the finding detail.');
        }
      }

      onCreated(finding);
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ??
        err?.message ??
        'Failed to raise finding.';
      setError(typeof msg === 'string' ? msg : 'Failed to raise finding.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnEscape data-testid="MOD_observation_root">
      <Modal.Header>Raise Finding</Modal.Header>

      <Modal.Body>
        {/* (a) read-only row context */}
        <div className="observation-context-card">
          <div className="observation-context-title">{rowContext.procedureName}</div>
          {rowContext.sampleLabel && (
            <div className="observation-context-row">
              Sample: <strong>{rowContext.sampleLabel}</strong>
            </div>
          )}
          {rowContext.responseValue && (
            <div className="observation-context-row">
              Response:{' '}
              <span className="observation-response-value">{rowContext.responseValue}</span>
            </div>
          )}
        </div>

        <form
          id="observation-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
        {/* (b) findings_text textarea */}
        <div className="observation-block">
          <ValidatedTextarea
            vh={fieldVh}
            name="findings_text"
            code={110}
            required
            label="Description"
            value={findingsText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFindingsText(e.target.value)
            }
            rows={4}
            data-testid="INP_observation_findings_text"
          />
          {trimmed.length > 0 && trimmed.length < MIN_FINDING_LENGTH && (
            <div className="text-xs text-gray-500 mt-1">
              {MIN_FINDING_LENGTH - trimmed.length} more character{
                MIN_FINDING_LENGTH - trimmed.length === 1 ? '' : 's'
              } required.
            </div>
          )}
        </div>

        {/* (c) Severity. Control/risk findings ALSO raise a deficiency/vulnerability,
            which carry a 5-level severity + an optional title — show those instead of
            the binary Material/Trivial toggle. */}
        {isRiskOrControl ? (
          <>
            <div className="observation-block">
              <FormControl.Input
                label={`${bridgeNoun} title (optional)`}
                value={bridgeTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBridgeTitle(e.target.value)}
                placeholder={`Short ${bridgeNoun.toLowerCase()} label`}
                data-testid="INP_observation_bridge_title"
              />
            </div>
            <div className="observation-block">
              <FormControl.Select
                label={`${bridgeNoun} severity`}
                value={bridgeSeverity}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBridgeSeverity(e.target.value as BridgeSeverity)}
                data-testid="SEL_observation_bridge_severity"
              >
                {BRIDGE_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </FormControl.Select>
            </div>
          </>
        ) : (
          <div className="observation-severity-row" role="radiogroup" aria-label="Severity">
            <div className="observation-section-label">Severity</div>
            <div className="severity-toggle-group">
              <button
                type="button"
                role="radio"
                aria-checked={!isTrivial}
                onClick={() => setIsTrivial(false)}
                className={`severity-toggle-btn${!isTrivial ? ' severity-toggle-btn--active-material' : ''}`}
                data-testid="RAD_observation_severity_material"
              >
                <AlertTriangle size={12} />
                Material
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={isTrivial}
                onClick={() => setIsTrivial(true)}
                className={`severity-toggle-btn${isTrivial ? ' severity-toggle-btn--active-trivial' : ''}`}
                data-testid="RAD_observation_severity_trivial"
              >
                Trivial
              </button>
            </div>
          </div>
        )}

        {/* (d) Evidence file slot — collapsible, collapsed by default */}
        <div className="observation-block">
          <button
            type="button"
            className="observation-evidence-toggle"
            onClick={() => setEvidenceOpen(v => !v)}
            disabled={submitting}
            data-testid="BTN_observation_evidence_toggle"
          >
            <Paperclip size={13} />
            <span>Attach Evidence</span>
            {evidenceFile && <span className="observation-evidence-badge">{evidenceFile.name}</span>}
            {evidenceOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {evidenceOpen && (
            <div className="observation-evidence-panel">
              <FormControl.FileUpload
                disabled={submitting}
                onChange={(files) => setEvidenceFile(files && files[0] ? files[0] : null)}
              />
            </div>
          )}
        </div>
        </form>

        {/* (e) error banner area */}
        {error && (
          <div className="inline-error inline-error--inline" role="alert">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="submit"
          form="observation-form"
          variant="fill"
          icon={Save}
          disabled={!canSubmit}
          data-testid="BTN_observation_submit"
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ObservationModal;
